/**
 * 2026-04-25 Phase 32-C.7 — 제련·각인 관련 공통 유틸.
 *
 * 모달·카드·UI 곳곳에서 반복되던 카운트/분류/라벨 로직을 한 곳으로.
 * 서버 응답만으로 계산 가능한 것만 여기. 서버 의존 계산은 별도 API.
 */
import type { InventoryItemView } from "@/api/types";

// ─── 재료 카운트 ─────────────────────────────────────────────────────

export const FORGE_ITEM_IDS = {
    pityToken: "material.pity_token",
    artisanAsh: "material.artisan_ash",
    repairStone: "material.repair_stone",
    refineStoneBasic: "material.refine_stone_basic",
    refineStoneRare: "material.refine_stone_rare",
    stabilityToken: "material.stability_token",
    polarOre: "material.polar_ore",
    sealStone: "material.seal_stone",
} as const;

export function countByItemId(items: InventoryItemView[] | undefined, itemId: string): number {
    if (!items) return 0;
    return items.reduce(
        (sum, i) => sum + (i.definition?.id === itemId ? i.quantity : 0),
        0,
    );
}

export function countPityTokens(items?: InventoryItemView[]): number {
    return countByItemId(items, FORGE_ITEM_IDS.pityToken);
}

export function countArtisanAsh(items?: InventoryItemView[]): number {
    return countByItemId(items, FORGE_ITEM_IDS.artisanAsh);
}

export function countRepairStones(items?: InventoryItemView[]): number {
    return countByItemId(items, FORGE_ITEM_IDS.repairStone);
}

/** 재료 전체 스냅샷 — UI 대시보드·헤더 동시 참조용. */
export interface ForgeMaterialSnapshot {
    pityTokens: number;
    artisanAsh: number;
    repairStones: number;
    refineStonesBasic: number;
    refineStonesRare: number;
    stabilityTokens: number;
    polarOres: number;
    sealStones: number;
}

export function computeMaterialSnapshot(items?: InventoryItemView[]): ForgeMaterialSnapshot {
    return {
        pityTokens: countByItemId(items, FORGE_ITEM_IDS.pityToken),
        artisanAsh: countByItemId(items, FORGE_ITEM_IDS.artisanAsh),
        repairStones: countByItemId(items, FORGE_ITEM_IDS.repairStone),
        refineStonesBasic: countByItemId(items, FORGE_ITEM_IDS.refineStoneBasic),
        refineStonesRare: countByItemId(items, FORGE_ITEM_IDS.refineStoneRare),
        stabilityTokens: countByItemId(items, FORGE_ITEM_IDS.stabilityToken),
        polarOres: countByItemId(items, FORGE_ITEM_IDS.polarOre),
        sealStones: countByItemId(items, FORGE_ITEM_IDS.sealStone),
    };
}

// ─── 구간 / 라벨 ─────────────────────────────────────────────────────

export type RefineBracket = 1 | 2 | 3;

export function bracketOfEnhancement(enhancement: number): RefineBracket {
    if (enhancement < 4) return 1;
    if (enhancement < 7) return 2;
    return 3;
}

export function bracketLabel(enhancement: number): string {
    const b = bracketOfEnhancement(enhancement);
    return b === 1 ? "🟢 입문 (+0~+4) — 실패해도 단계 유지"
        :  b === 2 ? "🟡 하락 (+4~+7) — 실패 시 -0~-2"
        :  "🔴 도전 (+7~+10) — 파괴 위험";
}

export function bracketShortLabel(enhancement: number): string {
    const b = bracketOfEnhancement(enhancement);
    return b === 1 ? "입문" : b === 2 ? "하락" : "도전";
}

/** 제련석 id — 구간별. */
export function stoneIdForEnhancement(enhancement: number): string {
    return bracketOfEnhancement(enhancement) === 3
        ? FORGE_ITEM_IDS.refineStoneRare
        : FORGE_ITEM_IDS.refineStoneBasic;
}

/** 구간별 기본 스텝 성공률 (백엔드와 동기화). */
export function stepSuccessRate(enhancement: number): number {
    return bracketOfEnhancement(enhancement) === 1 ? 70
        :  bracketOfEnhancement(enhancement) === 2 ? 55
        :  45;
}

// ─── 오라 판정 ──────────────────────────────────────────────────────

/** 오라 레벨 0~4. EquipmentAura 와 동기화 — UI 사전 판정에 사용. */
export function auraLevel(item: InventoryItemView): number {
    const e = item.enhancement ?? 0;
    const enhLevel = e >= 10 ? 4 : e >= 7 ? 3 : e >= 4 ? 2 : 0;
    const tierLevel = item.engraveTier === "GREAT" ? 3
                    : item.engraveTier === "SUCCESS" ? 1
                    : 0;
    return Math.max(enhLevel, tierLevel);
}

export function hasEquipmentAura(item: InventoryItemView): boolean {
    return auraLevel(item) > 0;
}

/** "자랑 장비" 판정 — 인벤토리 필터/강조 후보. */
export function isNotableItem(item: InventoryItemView): boolean {
    return auraLevel(item) >= 2 || (item.enhancement ?? 0) >= 7;
}

// ─── 각인 tier 라벨 ─────────────────────────────────────────────────

export function engraveTierLabel(tier?: "SUCCESS" | "GREAT" | null): string {
    return tier === "GREAT" ? "대성공" : tier === "SUCCESS" ? "성공" : "각인 없음";
}

export function engraveTierSymbol(tier?: "SUCCESS" | "GREAT" | null): string {
    return tier === "GREAT" ? "✦" : tier === "SUCCESS" ? "✧" : "";
}

// ─── 확률 도표용 톤 ─────────────────────────────────────────────────

/** critFail 확률에 따라 위험도 톤. UI 에서 색상 결정에 쓰임. */
export type RiskTone = "safe" | "nervous" | "bold" | "reckless";

export function critFailToTone(critFailPct: number): RiskTone {
    if (critFailPct <= 5) return "safe";
    if (critFailPct <= 15) return "nervous";
    if (critFailPct <= 30) return "bold";
    return "reckless";
}

export function riskToneLabel(tone: RiskTone): string {
    switch (tone) {
        case "safe":     return "안정적";
        case "nervous":  return "조마조마";
        case "bold":     return "걸어볼 만";
        case "reckless": return "도박";
    }
}

// ─── 장비 필터 / 정렬 ──────────────────────────────────────────────

/** 장비 카테고리 (WEAPON / ARMOR) 만 필터. */
export function onlyEquipment(items: InventoryItemView[]): InventoryItemView[] {
    return items.filter(i => i.definition?.category === "WEAPON" || i.definition?.category === "ARMOR");
}

/** enhancement 내림차순 정렬 (동점 시 tier GREAT > SUCCESS > null). */
export function sortByEnhancement(items: InventoryItemView[]): InventoryItemView[] {
    const tierScore = (t?: "SUCCESS" | "GREAT" | null) =>
        t === "GREAT" ? 2 : t === "SUCCESS" ? 1 : 0;
    return [...items].sort((a, b) => {
        const ea = a.enhancement ?? 0;
        const eb = b.enhancement ?? 0;
        if (ea !== eb) return eb - ea;
        return tierScore(b.engraveTier) - tierScore(a.engraveTier);
    });
}

/** 균열 보유 장비만. 수리석 UI 에서 "수리 대상" 목록 조회용. */
export function itemsWithCracks(items: InventoryItemView[]): InventoryItemView[] {
    return items.filter(i => (i.engraveCracks ?? 0) > 0);
}
